import * as THREE from 'three';
import { CAR_SHAPES, CAR_SHAPE_DEEP, ENV_CAR, ENV_LANDING, ENV_SCENE, LANDING_CUBE_MAP_SIZE, LANDING_CUBE_MAP_POSITION, ENV_IMAGE_CAPTURE } from '../../constants';
import { getCarShape } from '../blueprint/BlueprintProvider';
import { get3DQuality } from '../../utils/generalUtils';
import { QUALITY_3D_HIGH, CUSTOM_ENV_MAP_BLUR_STEPS } from './3d-constants'

// import { RenderTargetCache } from './CustomMipmapGenerator'

import VertexShader from './shader-lib/custom-physical/vert/custom_meshphysical_vert.glsl'
import FragmentShader from './shader-lib/custom-physical/frag/custom_meshphysical_frag.glsl'

// tonemapping names
import { ACESFilmicToneMapping, CineonToneMapping, ReinhardToneMapping, 
  NoToneMapping, LinearToneMapping } from 'three/src/constants'

export const NoToneMappingOverride = -1;

  // there must be a better way to determine these
export const KEYWORD_TONEMAPPING_LINEAR = "TONEMAP_LINEAR",
  KEYWORD_TONEMAPPING_REINHARD = "TONEMAP_REINHARD",
  KEYWORD_TONEMAPPING_CINEON = "TONEMAP_CINEON",
  KEYWORD_TONEMAPPING_ACES = "TONEMAP_ACES",
  KEYWORD_TONEMAPPING_NONE = "TONEMAP_NONE";

export const TONEMAPPING_KEYWORDS = [
  KEYWORD_TONEMAPPING_ACES, KEYWORD_TONEMAPPING_CINEON, 
  KEYWORD_TONEMAPPING_LINEAR, KEYWORD_TONEMAPPING_NONE, 
  KEYWORD_TONEMAPPING_REINHARD
];

const FRACTION_DIGITS = 8;

class ShaderBuilder {
  build(material, materialManager) {

    
    const {
      useUv2WithAlphaMap = true,
      useUv1WithAoMap = false,

      useTriplanarMapping = false,
      triplanarMappingScale = new THREE.Vector2(0.03, 0.03),

      useTpmForMap = false,
      tpmScaleForMap,

      useTpmForNormalMap = false,
      tpmScaleForNormalMap, 

      useTpmForRoughnessMap = false,
      tpmScaleForRoughnessMap,

      useTpmForMetalnessMap = false,
      tpmScaleForMetalnessMap,

      useTpmForBumpMap = false,
      tpmScaleForBumpMap,

      useTpmForEmissiveMap = false,
      tpmScaleForEmissiveMap,

      envMap, 
      textureRotation,

      anisotropy = 0,

      iridescent = false,
      // went the lazy way and reused map for iridescent
      //iridescenceMap
      
      // tonemapping uses internal three enum
      // use "tonemappingExposure: " if you want something else than 1.0
      toneMapping = -1,
      toneMappingExposure = 1.0,

      bloomIntensity = 0.0
    } = material.userData

    material.customProgramCacheKey = function() {
      // return 'custom: ' + shaderVariantOptions.join();
      return 'iridescent: ' + iridescent;      
    
    }

    material.onBeforeCompile = shader => {
      // we don't need to bother with materials that don't write anything to the color buffer
      //if (!material.colorWrite) return;

      shader.uniforms.uv2AlphaMap = { value: useUv2WithAlphaMap }
      shader.uniforms.uv1AOMap = { value: useUv1WithAoMap }      

      // TODO: make this non defined to skip shader compilation?
      if (iridescent){
        material.defines.IRIDESCENT = '';
      }

      shader.uniforms.orthographicCubemapping = materialManager.boxProjectionData.orthographic;
      shader.uniforms.reflectionBounces = materialManager.boxProjectionData.bounces;

      shader.uniforms.anisotropicReflections = { value: anisotropy > 0 }
      shader.uniforms.anisotropyStrength = { value: anisotropy }

      // if (
      // ) {
      //   material.defines.TILT_TRIPLANAR_MAPS = '';
      // }

      shader.uniforms.tpmData = {
        value: {
          useTpmMap: useTpmForMap || useTriplanarMapping,
          useTpmMetalnessMap: useTpmForMetalnessMap || useTriplanarMapping,
          useTpmRoughnessMap: useTpmForRoughnessMap || useTriplanarMapping,
          useTpmEmissiveMap: useTpmForEmissiveMap || useTriplanarMapping,
          useTpmNormalMap: useTpmForNormalMap || useTriplanarMapping,
          tilt: (useTriplanarMapping || useTpmForMap || useTpmForNormalMap || useTpmForRoughnessMap || useTpmForMetalnessMap || useTpmForBumpMap  || useTpmForEmissiveMap ) &&
          !isNaN(textureRotation) && textureRotation > 0
        }
      }

      shader.uniforms.mapTpmScale = { value: tpmScaleForMap ? this.toVec2(tpmScaleForMap, 'map tpm') : triplanarMappingScale };
      shader.uniforms.metalnessTpmScale = { value: tpmScaleForMetalnessMap ? this.toVec2(tpmScaleForMetalnessMap, 'metalness tpm') : triplanarMappingScale };
      shader.uniforms.roughnessTpmScale = { value: tpmScaleForRoughnessMap ? this.toVec2(tpmScaleForRoughnessMap, 'roughness tpm') : triplanarMappingScale };
      shader.uniforms.emissiveTpmScale = { value: tpmScaleForEmissiveMap ? this.toVec2(tpmScaleForEmissiveMap, 'emissive tpm') : triplanarMappingScale };
      shader.uniforms.normalTpmScale = { value: tpmScaleForNormalMap ? this.toVec2(tpmScaleForNormalMap, 'normal tpm') : triplanarMappingScale };

      let envTarget = envMap;
      switch (envTarget) {
        case ENV_IMAGE_CAPTURE:
          envTarget = ENV_CAR;
        // eslint-disable-next-line no-fallthrough
        case ENV_CAR:
          shader.uniforms.useBoxProjection = { value: true };
          shader.uniforms.boxProjectionSize = materialManager.boxProjectionData.carSize;
          shader.uniforms.boxProjectionPosition = materialManager.boxProjectionData.carPosition;

          shader.uniforms.diffuseCubemapArray = materialManager.orthoEnvTextures[envTarget].mainArray;
          shader.uniforms.ormCubemap = materialManager.orthoEnvTextures[envTarget].orm;
          shader.uniforms.normalCubemap = materialManager.orthoEnvTextures[envTarget].normal;
          shader.uniforms.standardCubemap = materialManager.standardCarEnvTexture;
          break;
        case ENV_LANDING:
          shader.uniforms.useBoxProjection = { value: true };
          shader.uniforms.boxProjectionSize = materialManager.boxProjectionData.landingSize;
          shader.uniforms.boxProjectionPosition = materialManager.boxProjectionData.landingPosition;
          shader.uniforms.ormCubemap = materialManager.orthoEnvTextures[envTarget].orm;         
          shader.uniforms.normalCubemap = materialManager.orthoEnvTextures[envTarget].normal;
          shader.uniforms.standardCubemap = materialManager.standardLandingEnvTexture;
          break;
          /*
        case ENV_IMAGE_CAPTURE:
          shader.uniforms.boxProjectionSize = materialManager.boxProjectionData.carSize;
          shader.uniforms.boxProjectionPosition = materialManager.boxProjectionData.carPosition;            
          break;
          */
        case undefined:
          shader.uniforms.useBoxProjection = { value: false };
          break;
        default:
          console.error('Unknown environment mapping setup for', material.name, 'value:', envMap)
          break;
      }

      // Add uv transformation matrix uniforms (
      // Note: material.map uv transformation matrix is already set by default (see. uvTransform)
      [
        'alphaMap',
        'bumpMap',
        'emissiveMap',    
        'metalnessMap',
        'normalMap',
        'roughnessMap',
      ].forEach(mapType => {
        if (material[mapType]) {
          const uvTransformMatrix = new THREE.Matrix3();
          uvTransformMatrix.setUvTransform( material[mapType].offset.x, material[mapType].offset.y, material[mapType].repeat.x, material[mapType].repeat.y, material[mapType].rotation, material[mapType].center.x, material[mapType].center.y );
          const uniformName = mapType + 'UvTransform'
          shader.uniforms[ uniformName ] = { value: uvTransformMatrix };
        }
      })

      shader.uniforms.tonemappingData = {
        value: {
          type: parseInt(toneMapping),
          exposure: toneMappingExposure
        }
      }

      
      shader.uniforms.defaultNormalCubemap = materialManager.defaultNormalCubemap;
      shader.uniforms.emissionOnly = materialManager.emissionOnly;
      shader.uniforms.diffuseOnly = materialManager.diffuseOnly;
      shader.uniforms.ormOnly = materialManager.ormOnly;
      shader.uniforms.normalOnly = materialManager.normalOnly;
      shader.uniforms.opacityToR = materialManager.opacityToR;
      shader.uniforms.noEnvironmentMapping = materialManager.noEnvironmentMapping;
      shader.uniforms.reflectionDebug = materialManager.reflectionDebug;
      shader.uniforms.anisotropyEnabled = materialManager.anisotropyEnabled;
      shader.uniforms.anisotropyDebug = materialManager.anisotropyDebug;
      shader.uniforms.overblowDebug = materialManager.overblowDebug;
      shader.uniforms.magic1 = materialManager.magic1;
      shader.uniforms.magic2 = materialManager.magic2;
      shader.uniforms.magic3 = materialManager.magic3;

      shader.uniforms.use_bumpmap = material.bumpMap !== undefined;

      if (shader.uniforms.bloomIntensity === undefined){
        shader.uniforms.bloomIntensity = {value: bloomIntensity}
      } else {
        shader.uniforms.bloomIntensity.value = bloomIntensity
      }

      
      shader.vertexShader = VertexShader;
      shader.fragmentShader = FragmentShader;
      material.shader = shader

      // console.trace("Rebuilt shader: ", material.name);
    }
  }

  toVec2(value, valueName){
    
    let ret = undefined;
    if (value === undefined){
      ret = new THREE.Vector2(1, 1);
    }
    if (Array.isArray(value)){
      ret = new THREE.Vector2(value[0], value[1]);
    } else {
      console.warn(valueName, 'had unexpected type, changing to vec2 array')
      ret = new THREE.Vector2(value, value);
    }
    // console.log('tovec2', valueName, value, '->', ret)
    return ret;
  }
}

export default ShaderBuilder;